import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import BackButton from "../../Components/BackButton";
import { Success, Error } from "../../Components/Modal";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { promoSchema } from "../../Schema/generalSchema";
import { useMutation, useQuery } from "react-query";
import { getAllUsers } from "../../Services/User";
import { image_url } from "../../Utils/connection_strings";
import PromoForm from "./Components/PromoForm";
import { getPromoCodeDetails, editPromoCode } from "../../Services/PromoCode";
import "../CMSManagement/CMSManagement.css";
import "./PromoCodes.css";

export default function EditPromoCode() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [image, setImage] = useState(null);
    const [preview, setPreview] = useState(null);

    const { register, handleSubmit, formState: { errors }, control, setValue, watch, reset, trigger } = useForm({
        resolver: yupResolver(promoSchema),
    });

    // Fetch Users for targeting
    const { data: userData } = useQuery(["all-users-promo"], () => getAllUsers(1, 1000));
    const users = userData?.data?.data?.docs || [];

    const { isLoading: isFetching } = useQuery(
        ["promo-details", id],
        () => getPromoCodeDetails(id),
        {
            onSuccess: (res) => {
                const promo = res.data?.data?.docs?.[0] || res.data?.data; // Check structure
                if (promo) {
                    reset({
                        promoCode: promo.promoCode,
                        discountType: promo.discountType,
                        discountValue: promo.value,
                        startDate: promo.startDate ? promo.startDate.split('T')[0] : "",
                        expiryDate: promo.expiryDate ? promo.expiryDate.split('T')[0] : "",
                        minOrderValue: promo.minOrderValue,
                        maxDiscountCap: promo.maxDiscountCap,
                        usageLimit: promo.usageLimitGlobal > 0 ? promo.usageLimitGlobal : "",
                        usageLimitPerUser: promo.usageLimitPerUser ?? 1,
                        status: promo.visibilityStatus === "active" ? "true" : "false",
                        firstOrderOnly: promo.firstOrderOnly,
                        users: promo.applyOnUsers || [],
                    });

                    if (promo.promoBanner && !image) {
                        const clean_url = image_url?.endsWith("/") ? image_url.slice(0, -1) : image_url;
                        const fullUrl = promo.promoBanner.startsWith('http')
                            ? promo.promoBanner
                            : `${clean_url}/${promo.promoBanner}`;
                        setPreview(fullUrl);
                    }
                }
            },
            refetchOnWindowFocus: false,
        }
    );

    const { mutate, isLoading: isUpdating } = useMutation((formData) => {
        return editPromoCode(formData);
    }, {
        onSuccess: (res) => {
            Success("Update Successful", res.data?.message || "Promotion configuration has been updated.");
            navigate("/promo-codes");
        },
        onError: (err) => Error(err?.response?.data?.message || "Communication failure during update.")
    });

    const handleImageChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setImage(file);
            setPreview(URL.createObjectURL(file));
        }
    };

    const onSubmit = (data) => {
        const formData = new FormData();
        formData.append("id", id);
        formData.append("typeName", "promoCodes");
        formData.append("promoCode", data.promoCode);
        formData.append("discountType", data.discountType);
        formData.append("value", data.discountValue);
        formData.append("minOrderValue", data.minOrderValue || 0);
        formData.append("maxDiscountCap", data.maxDiscountCap || 0);
        formData.append("applyOnUsers", JSON.stringify(data.users || []));
        formData.append("firstOrderOnly", data.firstOrderOnly);
        formData.append("usageLimitGlobal", data.usageLimit || 0);
        formData.append(
            "usageLimitPerUser",
            data.usageLimitPerUser === null || data.usageLimitPerUser === undefined ? 1 : data.usageLimitPerUser
        );
        formData.append("startDate", data.startDate);
        formData.append("expiryDate", data.expiryDate);
        formData.append("visibilityStatus", data.status === "true" ? "active" : "inactive");

        if (image) formData.append("promoBanner", image);
        mutate(formData);
    };

    return (
        <div className="app-content content promo-page">
            <div className="content-wrapper">
                <div className="content-body">
                    <section id="edit-promo-code">
                        <div className="user-details-wrapper">
                            <div className="promo-page-header">
                                <div className="back-title d-flex align-items-center mb-2">
                                    <BackButton />
                                    <h1 className="promo-page-header__title ms-3">Edit Promotion Settings</h1>
                                </div>
                                <p className="promo-page-header__subtitle ms-5 ps-2">
                                    Update discount, audience, and schedule for this promo code
                                </p>
                            </div>

                            {isFetching ? (
                                <div className="promo-section text-center py-5">
                                    <div className="spinner-border text-warning mb-3" role="status" />
                                    <p className="text-muted fw-bold mb-0">Loading promotion data...</p>
                                </div>
                            ) : (
                                <PromoForm
                                    register={register}
                                    handleSubmit={handleSubmit}
                                    errors={errors}
                                    control={control}
                                    onSubmit={onSubmit}
                                    isLoading={isUpdating}
                                    isEdit={true}
                                    preview={preview}
                                    handleImageChange={handleImageChange}
                                    users={users}
                                    setValue={setValue}
                                    watch={watch}
                                    trigger={trigger}
                                />
                            )}
                        </div>
                    </section>
                </div>
            </div>
        </div>
    );
}
